// ================================================= /
// ======   Slider JS   ============================ /
// ================================================= /

/* eslint-disable max-statements, complexity */

/* global jQuery */

import { throttle, debounce } from 'throttle-debounce';
import gsap from 'gsap';
import Draggable from "gsap/Draggable";

import helpers from './helpers';

/**
 * Class for controlling the slider element
 */
class DispatchSkinSlider {

	/**
	 * Constructs the class and sets up instance variables.
	 */
	constructor() {

		this.selector = '.js-slider';

		this.reelSelector = `${this.selector}__reel`;
		this.slideSelector = `${this.selector}__slide`;
		this.cardLinkSelector = `${this.selector}__card-link`;
		this.controlSelector = `${this.selector}__control`;

		this.eventNamespace = 'dispatchSkinSlider';

		this.indexDataAttribute = 'index';
		this.lockedDataAttribute = 'is-locked';
		this.directionDataAttribute = 'direction';
		this.slideDurationDataAttribute = 'slide-duration';
		this.slideStaggerDataAttribute = 'slide-stagger';

		this.passedClass = 'has-passed';
		this.activeClass = 'is-active';
		this.comingClass = 'is-coming';

		this.slideDirection = 'horizontal';
		this.touchMoveThrottle = 18;
		this.touchVelocityScalingFactor = 0.66666666666;
		this.touchAcceleration = 6500;
		this.resizeDebounce = 100;
		this.slideDuration = 0.2;
		this.slideStagger = 0.05;
		this.touchMoveThreshold = 35;
		this.touchTimeThreshold = 1000;

		this.sliders = [];

		this.touchedSlider = null;
		this.touchTime = null;
		this.hasBeenTouched = false;
		this.isSnapping = false;
		this.touchTimeout = null;
		this.firstTouchX = 0;
		this.previousTouchX = 0;
		this.currentTouchX = 0;
		this.lastTouchX = 0;
		this.dTouchX = 0;

		this.hooks = {};
	}

	/**
	 * Initializes the class, sets up events.
	 */
	initialize() {

		this.sliders = jQuery( this.selector );

		if ( this.sliders.length ) {

			this.events();
		}
	}

	/**
	 * Wires up events
	 */
	events() {

		this.eventDelegationParent = jQuery( '.site' );

		// Control click
		this.eventDelegationParent.on(
			`vclick.${this.eventNamespace}ControlClick`,
			this.controlSelector,
			this.handleControlClick.bind( this )
		);

		// Swipe

		this.addHook(
			'after',
			this.selector,
			this.resetTouchState.bind( this )
		);

		this.eventDelegationParent.on(
			`touchstart.${this.eventNamespace}TouchStart`,
			this.reelSelector,
			this.handleTouchStart.bind( this )
		);

		this.sliders.find( this.reelSelector )
			.each(
				function( index, reel ) {

					reel.addEventListener(
						'touchmove',
						throttle( this.touchMoveThrottle, this.handleTouchMove.bind( this ) ),
						{ passive: false }
					);
				}.bind( this )
			);

		this.eventDelegationParent.on(
			`touchend.${this.eventNamespace}TouchEnd`,
			this.reelSelector,
			this.handleTouchEnd.bind( this )
		);

		// Resize
		jQuery( window ).on(
			`resize.${this.eventNamespace}`,
			debounce( this.resizeDebounce, this.handleResize.bind( this ) )
		);
	}

	/* Event handlers --------------------------------------- */

	/**
	 * Handles the control click.
	 */
	handleControlClick( event ) {

		if ( this.hasBeenTouched || this.isSnapping ) {
			return;
		}

		const activeControl = jQuery( event.currentTarget );
		const slider = activeControl.closest( this.selector );

		let toIndex = this.getActiveIndex( activeControl );
		const direction = activeControl.data( this.directionDataAttribute );

		if ( direction ) {
			toIndex = this.getActiveIndex( slider ) + parseInt( direction, 10 );
		}

		this.changeSlide(
			slider,
			toIndex
		);
	}

	/**
	 * Handles touch start
	 */
	handleTouchStart( event ) {

		this.touchedSlider = jQuery( event.currentTarget ).closest( this.selector );

		if ( this.isSnapping || ! this.doHooks( 'beforeTouch', this.touchedSlider ) ) {
			return;
		}

		this.touchTime = Date.now();
		this.firstTouchX = event.originalEvent.targetTouches[0].clientX;
		this.previousTouchX = this.firstTouchX;
		this.currentTouchX = this.firstTouchX;
	}

	/**
	 * Handles the touchmove.
	 */
	handleTouchMove( event ) {

		if ( this.isSnapping || ! this.doHooks( 'beforeTouch', this.touchedSlider ) ) {
			return true;
		}

		// Set the touch coordinates

		const touchX = event.targetTouches[0].clientX;
		const dTouchX = touchX - this.currentTouchX;

		this.previousTouchX = this.currentTouchX;
		this.currentTouchX = touchX;
		this.dTouchX += dTouchX;

		// If we're touching now, reset the classes

		if ( ! this.hasBeenTouched ) {

			const hasMovedPastThreshold = Math.abs( this.dTouchX ) > this.touchMoveThreshold;
			const hasBeenTouchedPastThreshold = Date.now() - this.touchTime > this.touchTimeThreshold;

			if ( this.touchedSlider && ( hasMovedPastThreshold || ! hasMovedPastThreshold && hasBeenTouchedPastThreshold ) ) {

				this.touchedSlider.find( this.slideSelector )
					.removeClass( this.comingClass )
					.removeClass( this.passedClass )
					.removeClass( this.activeClass );

				this.hasBeenTouched = true;

				if ( event.cancelable ) {
					event.preventDefault();
				}
			}
		}

		else {

			if ( event.cancelable ) {
				event.preventDefault();
			}

			if ( ! this.isSnapping ) {

				gsap.to(
					this.touchedSlider.find( this.slideSelector ),
					{
						duration: this.touchMoveThrottle / 1000,
						x: '+=' + dTouchX
					}
				);
			}

			window.clearTimeout( this.touchTimeout );

			this.touchTimeout = window.setTimeout(
				function() {
					this.previousTouchX = this.currentTouchX;
				}.bind( this ),
				this.touchMoveThrottle
			);
		}

		return true;
	}

	/**
	 * Handles the touchend.
	 */
	handleTouchEnd() {

		if ( ! this.hasBeenTouched || this.isSnapping || ! this.doHooks( 'beforeTouch', this.touchedSlider ) ) {
			this.resetTouchState();
			return;
		}

		this.isSnapping = true;

		const slides = this.touchedSlider.find( this.slideSelector );

		const offsets = this.getSlideOffsets( slides );
		const currentOffset = offsets[this.getActiveIndex( this.touchedSlider )];

		// So now we calculate how far the slides move and how long it takes

		let vThrow = ( this.currentTouchX - this.previousTouchX ) * 1000 * this.touchVelocityScalingFactor / this.touchMoveThrottle;
		const aThrow = this.touchAcceleration * ( vThrow > 0 ? -1 : 1 );
		let dThrow = Math.pow( vThrow, 2 ) * -1 / aThrow * 2;

		// We need to constrain the amount one can throw this thing

		const windowThrowBuffer = window.innerWidth / 4;

		const maxDThrow = dThrow > 0
			? Math.max.apply( null, offsets ) - currentOffset + windowThrowBuffer
			: currentOffset * -1 - windowThrowBuffer;

		dThrow = Math[dThrow > 0 ? 'min' : 'max'](
			dThrow,
			maxDThrow
		);

		vThrow = Math.sqrt( -2 * aThrow * dThrow );

		const tThrow = Math.abs( dThrow * 2 / vThrow );

		// And then figure out which slide is the closest

		const dOffsets = offsets.map(
			( offset ) => Math.abs( this.dTouchX + dThrow + currentOffset - offset )
		);

		const toIndex = dOffsets.indexOf( Math.min.apply( null, dOffsets ) );

		// Then we move the slides
		dThrow = dThrow === -0
			? 0
			: dThrow;

		gsap.to(
			slides,
			{
				duration: tThrow,
				x: '+=' + dThrow,
				onComplete: function() {
					this.changeSlide( this.touchedSlider, toIndex, true, false );
				}.bind( this )
			}
		);
	}

	/**
	 * Handles resize.
	 */
	handleResize() {

		this.sliders.each(
			function( index, slider ) {
				slider = jQuery( slider );
				this.changeSlide( slider, this.getActiveIndex( slider ), true );
			}.bind( this )
		);
	}

	/* Slider methods --------------------------------------- */

	/**
	 * Changes the active slide.
	 */
	changeSlide( slider, toIndex, animate = true, stagger = true ) {

		if ( ! this.doHooks( 'beforeChangeSlide', slider, toIndex ) ) {
			return;
		}

		const slides = slider.find( this.slideSelector );

		if ( toIndex === -1 ) {
			toIndex = slides.length - 1;
		}

		else if ( toIndex === slides.length ) {
			toIndex = 0;
		}

		if ( ! this.doHooks( 'beforeChangeIndex', slider, toIndex ) ) {
			return;
		}

		// Do the index dance

		const fromIndex = this.getActiveIndex( slider );
		slider.data( this.indexDataAttribute, toIndex );

		// Set the active control

		slider.find( this.controlSelector )
			.removeClass( this.activeClass )
			.eq( toIndex )
			.addClass( this.activeClass );

		if ( ! this.doHooks( 'beforeAnimateChangeSlide', slider, toIndex ) ) {
			return;
		}

		// Finally, we only move if the slider isn't locked
		if ( slider.data( this.lockedDataAttribute ) ) {
			return;
		}

		// Now, we move the slide

		slider.data( this.lockedDataAttribute, true );

		if ( toIndex === -1 ) {
			toIndex = slides.length - 1;
		}

		else if ( toIndex === slides.length ) {
			toIndex = 0;
		}

		if ( this.doHooks( 'beforeSetState', slider, toIndex ) ) {

			slides.each(
				function( index, slide ) {
					this.setSlideState( jQuery( slide ), index, toIndex );
				}.bind( this )
			);
		}

		if ( ! this.doHooks( 'afterSetState', slider, toIndex ) ) {
			slider.data( this.lockedDataAttribute, false );
			this.resetTouchState();
			return;
		}

		const slideDirection = slider.data( this.directionDataAttribute ) || this.slideDirection;
		const goingVertical = slideDirection === 'vertical';
		const goingReverse = slideDirection === 'reverse-vertical' || slideDirection === 'reverse-horizontal';

		const slideDuration = parseFloat( slider.data( this.slideDurationDataAttribute ) || this.slideDuration );

		let translate = 0;

		if ( toIndex !== 0 ) {
			translate = this.getSlideOffsets( slides, goingVertical )[toIndex];
		}

		const animationProps = {
			duration: slideDuration,
			ease: 'power1.inOut',
			onComplete: function() {
				slider.data( this.lockedDataAttribute, false );
				this.doHooks( 'after', slider, toIndex );
			}.bind( this )
		};

		if ( stagger ) {

			delete animationProps.duration;

			let staggerIndex = 0;

			const slideStagger = Math.min(
				slides.length * parseFloat( slider.data( this.slideStaggerDataAttribute ) || this.slideStagger ),
				slideDuration
			);

			animationProps.stagger = {
				amount: stagger ? slideStagger : 0,
				from: fromIndex,
				onComplete: function() {
					this.doHooks( 'stagger', slider, slides, staggerIndex, toIndex );
					staggerIndex++;
				}.bind( this )
			};
		}

		animationProps[goingVertical ? 'y' : 'x'] = goingReverse
			? -1 * translate
			: translate;

		gsap[animate ? 'to' : 'set'](
			slides,
			animationProps
		);
	}

	/**
	 * Gets the active index.
	 */
	getActiveIndex( element ) {

		return parseInt( element.data( this.indexDataAttribute ), 10 );
	}

	/* Hook methods ----------------------------------------- */

	/**
	 * Adds a hook.
	 */
	addHook( hookName, selector, callback ) {

		if ( ! this.hooks[hookName] ) {
			this.hooks[hookName] = [];
		}

		this.hooks[hookName].push(
			{
				selector: selector,
				callback: callback
			}
		);
	}

	/**
	 * Gets the hooks as a callback.
	 */
	getHookCallback( hookName, slider, ...hookArgs ) {

		return () => this.doHooks( hookName, slider, ...hookArgs );
	}

	/**
	 * Does the hooks.
	 */
	doHooks( hookName, slider, ...hookArgs ) {

		if ( this.hooks[hookName] ) {

			for ( const hook of this.hooks[hookName] ) {

				if ( slider.is( hook.selector ) && hook.callback.call( null, slider, ...hookArgs ) === false ) {
					return false;
				}
			}
		}

		return true;
	}

	/* Helper methods --------------------------------------- */

	/**
	 * Resets touch state.
	 */
	resetTouchState() {

		this.touchedSlider = null;
		this.touchTime = null;
		this.hasBeenTouched = false;
		this.isSnapping = false;
		this.touchTimeout = null;
		this.firstTouchX = 0;
		this.previousTouchX = 0;
		this.currentTouchX = 0;
		this.lastTouchX = 0;
		this.dTouchX = 0;
	}

	/**
	 * Sets the slide state.
	 */
	setSlideState( slide, index, toIndex ) {

		slide.removeClass( this.passedClass )
			.removeClass( this.activeClass )
			.removeClass( this.comingClass );

		if ( index > toIndex ) {
			slide.addClass( this.comingClass );
		}
		else if ( index < toIndex ) {
			slide.addClass( this.passedClass );
		}
		else {
			slide.addClass( this.activeClass );
		}
	}

	/**
	 * Returns the offsets of all slides relative to the first one.
	 */
	getSlideOffsets( slides, goingVertical = false, ) {

		const offsets = [];

		const firstSlideEdge = slides[0].getBoundingClientRect()[goingVertical ? 'top' : 'right'];

		slides.each(
			function( index ) {
				offsets.push( firstSlideEdge - slides[index].getBoundingClientRect()[goingVertical ? 'top' : 'right'] );
			}
		);

		return offsets;
	}

}

const slider = new DispatchSkinSlider();
export default slider;



// WEBPACK FOOTER //
// ./src/js/inc/slider.js